import { GameState } from '../types';
import { t, setLanguage } from '../i18n/I18n';
import { SaveManager } from '../save/SaveManager';
import { WEAPONS } from '../data/weaponData';
import { STAGES } from '../data/stageData';
import { UPGRADE } from '../constants';
import { EventEmitter } from '../core/EventEmitter';

export class UIManager {
  private overlay: HTMLElement;
  private events: EventEmitter;
  private saveManager: SaveManager;
  private currentScreen: HTMLElement | null = null;
  private hudElement: HTMLElement | null = null;

  // Weapon select state
  private wsSlots = ['pistol', '', ''];

  constructor(events: EventEmitter, saveManager: SaveManager) {
    this.overlay = document.getElementById('ui-overlay')!;
    this.events = events;
    this.saveManager = saveManager;

    // Single global click handler using event delegation
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const action = target.getAttribute('data-action');
      if (!action) return;

      e.stopPropagation();
      e.preventDefault();
      this.handleAction(action, target);
    });
  }

  private handleAction(action: string, target: HTMLElement): void {
    // Dynamic stage cutscene test buttons
    const stageMatch = action.match(/^debug-play-stage-(\d+)$/);
    if (stageMatch) {
      this.events.emit('ui:debugCutscene', { type: 'stage', stage: parseInt(stageMatch[1]) });
      return;
    }

    switch (action) {
      // Home
      case 'go-play':
        this.events.emit('ui:navigate', GameState.WEAPON_SELECT);
        break;
      case 'go-shop':
        this.showShop();
        break;
      case 'go-settings':
        this.showSettings();
        break;
      case 'debug-escape-cutscene':
        this.events.emit('ui:debugEscapeCutscene');
        break;
      case 'debug-cutscene-menu':
        this.showCutsceneTestMenu();
        break;
      case 'debug-play-intro':
        this.events.emit('ui:debugCutscene', { type: 'intro' });
        break;
      case 'debug-play-escape':
        this.events.emit('ui:debugCutscene', { type: 'escape' });
        break;
      case 'debug-back-home':
        this.events.emit('ui:navigate', GameState.HOME);
        break;

      // Weapon Select
      case 'ws-slot-pick': {
        this.activeWsSlot = parseInt(target.getAttribute('data-slot') ?? '0');
        this.showWeaponSelect();
        break;
      }
      case 'ws-select': {
        const wId = target.getAttribute('data-weapon')!;
        const slotIdx = parseInt(target.getAttribute('data-slot') ?? '0');
        const existingSlot = this.wsSlots.indexOf(wId);
        if (existingSlot >= 0 && existingSlot !== slotIdx) {
          this.wsSlots[existingSlot] = this.wsSlots[slotIdx];
        }
        this.wsSlots[slotIdx] = wId;
        this.showWeaponSelect();
        break;
      }
      case 'ws-remove': {
        const rmSlot = parseInt(target.getAttribute('data-slot') ?? '0');
        // Don't allow removing if it's the only weapon
        const filledSlots = this.wsSlots.filter(s => s !== '').length;
        if (filledSlots > 1) {
          this.wsSlots[rmSlot] = '';
        }
        this.showWeaponSelect();
        break;
      }
      case 'ws-confirm':
        this.events.emit('ui:weaponSelected', { slot1: this.wsSlots[0], slot2: this.wsSlots[1], slot3: this.wsSlots[2] });
        this.events.emit('ui:navigate', GameState.STAGE_SELECT);
        break;
      case 'ws-back':
        this.events.emit('ui:navigate', GameState.HOME);
        break;

      // Stage Select
      case 'play-level': {
        const stage = parseInt(target.getAttribute('data-stage')!);
        const level = parseInt(target.getAttribute('data-level')!);
        this.events.emit('ui:levelSelected', { stage, level });
        break;
      }
      case 'ss-back':
        this.events.emit('ui:navigate', GameState.WEAPON_SELECT);
        break;

      // Pause
      case 'pause-game':
        this.events.emit('ui:navigate', GameState.PAUSED);
        break;
      case 'resume-game':
        this.events.emit('ui:resume');
        break;
      case 'pause-settings':
        this.showSettings(true);
        break;
      case 'pause-home':
        this.events.emit('ui:navigate', GameState.HOME);
        break;

      // Level Clear
      case 'next-level': {
        let nextStage = parseInt(target.getAttribute('data-stage')!);
        let nextLevel = parseInt(target.getAttribute('data-level')!);
        this.events.emit('ui:navigate', GameState.WEAPON_SELECT);
        this.events.emit('ui:nextLevel', { stage: nextStage, level: nextLevel });
        break;
      }
      case 'clear-home':
        this.events.emit('ui:navigate', GameState.HOME);
        break;

      // Game Over
      case 'retry':
        this.events.emit('ui:retry');
        break;
      case 'gameover-home':
        this.events.emit('ui:navigate', GameState.HOME);
        break;

      // Shop
      case 'shop-buy': {
        const wId = target.getAttribute('data-weapon')!;
        const price = WEAPONS[wId].price;
        if (this.saveManager.purchaseWeapon(wId, price)) {
          this.showShop('buy');
        }
        break;
      }
      case 'shop-upgrade': {
        const wId = target.getAttribute('data-weapon')!;
        const level = this.saveManager.getWeaponLevel(wId);
        const cost = UPGRADE.COSTS[level];
        if (this.saveManager.upgradeWeapon(wId, cost)) {
          this.showShop('upgrade');
        }
        break;
      }
      case 'shop-tab-buy':
        this.showShop('buy');
        break;
      case 'shop-tab-upgrade':
        this.showShop('upgrade');
        break;
      case 'shop-back':
        this.events.emit('ui:navigate', GameState.HOME);
        break;

      // Settings
      case 'set-lang': {
        const lang = target.getAttribute('data-lang') as 'ja' | 'en';
        this.saveManager.setSetting('language', lang);
        setLanguage(lang);
        this.showSettings(target.getAttribute('data-from-pause') === 'true');
        break;
      }
      case 'set-diff': {
        const diff = target.getAttribute('data-diff') as any;
        this.saveManager.setSetting('difficulty', diff);
        this.showSettings(target.getAttribute('data-from-pause') === 'true');
        break;
      }
      case 'settings-back': {
        const fromPause = target.getAttribute('data-from-pause') === 'true';
        if (fromPause) {
          this.events.emit('ui:navigate', GameState.PAUSED);
        } else {
          this.events.emit('ui:navigate', GameState.HOME);
        }
        break;
      }
    }
  }

  showScreen(state: GameState, data?: any): void {
    this.clearScreen();
    switch (state) {
      case GameState.HOME: this.showHome(); break;
      case GameState.WEAPON_SELECT: this.showWeaponSelect(); break;
      case GameState.STAGE_SELECT: this.showStageSelect(); break;
      case GameState.PLAYING: this.showHUD(); break;
      case GameState.PAUSED: this.showPause(); break;
      case GameState.LEVEL_CLEAR: this.showLevelClear(data); break;
      case GameState.GAME_OVER: this.showGameOver(); break;
    }
  }

  clearScreen(): void {
    // Remove current screen
    if (this.currentScreen) {
      this.currentScreen.remove();
      this.currentScreen = null;
    }
    // Also remove ALL children from overlay to prevent leftovers
    while (this.overlay.firstChild) {
      this.overlay.removeChild(this.overlay.firstChild);
    }
    this.hudElement = null;
    this.minimapCtx = null;
  }

  private setScreen(html: string): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;';
    container.innerHTML = html;
    this.overlay.appendChild(container);
    this.currentScreen = container;
    return container;
  }

  // ==================== HOME ====================
  private showHome(): void {
    this.setScreen(`
      <div style="width:100%;height:100%;background:linear-gradient(180deg,#0a0a0a 0%,#1a0000 50%,#0a0a0a 100%);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1.5rem;">
        <div style="position:absolute;top:20px;right:20px;color:#ffd700;font-size:1.2rem;">
          ${t('shop.coins')}: ${this.saveManager.getCoins()}
        </div>
        <div style="font-size:4rem;font-weight:bold;color:#ff0000;text-shadow:0 0 30px rgba(255,0,0,0.6),0 0 60px rgba(255,0,0,0.3);letter-spacing:0.4em;margin-bottom:2rem;">
          ${t('game.title')}
        </div>
        <button data-action="go-play" style="width:280px;padding:15px 0;background:rgba(255,0,0,0.15);border:2px solid #ff0000;color:#ff4444;font-size:1.3rem;font-weight:bold;cursor:pointer;letter-spacing:0.2em;">
          ${t('home.play')}
        </button>
        <button data-action="go-shop" style="width:280px;padding:15px 0;background:rgba(255,0,0,0.15);border:2px solid #ff0000;color:#ff4444;font-size:1.3rem;font-weight:bold;cursor:pointer;letter-spacing:0.2em;">
          ${t('home.shop')}
        </button>
        <button data-action="go-settings" style="width:280px;padding:15px 0;background:rgba(255,0,0,0.15);border:2px solid #ff0000;color:#ff4444;font-size:1.3rem;font-weight:bold;cursor:pointer;letter-spacing:0.2em;">
          ${t('home.settings')}
        </button>
        <button data-action="debug-cutscene-menu" style="width:280px;padding:10px 0;background:rgba(100,100,0,0.15);border:1px solid #666;color:#888;font-size:0.85rem;cursor:pointer;margin-top:1rem;">
          [TEST] カットシーン確認
        </button>
      </div>
    `);
  }

  // ==================== CUTSCENE TEST MENU ====================
  private showCutsceneTestMenu(): void {
    const btnStyle = 'width:200px;padding:8px 0;background:rgba(255,255,255,0.05);border:1px solid #555;color:#ccc;font-size:0.9rem;cursor:pointer;';
    const stageBtns = Array.from({ length: 10 }, (_, i) => {
      const s = i + 1;
      const names = ['Dr. Zero', 'Mother Zombie', 'Armored Beast', 'Toxic King', 'Cyber Zombie', 'Hive Mind', 'Flesh Golem', 'Shadow Stalker', 'General Dead', 'Apocalypse Zombie'];
      return `<button data-action="debug-play-stage-${s}" style="${btnStyle}">S${s}: ${names[i]}</button>`;
    }).join('');

    this.setScreen(`
      <div style="width:100%;height:100%;background:rgba(0,0,0,0.95);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.5rem;overflow-y:auto;padding:20px;">
        <div style="font-size:1.5rem;color:#ffcc00;margin-bottom:0.5rem;">カットシーン テスト</div>
        <button data-action="debug-play-intro" style="${btnStyle}">イントロ</button>
        <button data-action="debug-play-escape" style="${btnStyle}">エスケープ (S1)</button>
        <div style="color:#888;font-size:0.8rem;margin:0.5rem 0;">── ステージクリア ──</div>
        ${stageBtns}
        <button data-action="debug-back-home" style="${btnStyle};margin-top:1rem;border-color:#ff4444;color:#ff4444;">戻る</button>
      </div>
    `);
  }

  // ==================== WEAPON SELECT (3 slots) ====================
  private activeWsSlot = 0; // which slot is being edited

  private showWeaponSelect(): void {
    const weapons = this.saveManager.getData().playerData.weapons;
    const ownedWeapons = Object.entries(weapons).filter(([, v]) => v.owned);

    // Ensure valid slot 1
    if (!this.wsSlots[0] || !weapons[this.wsSlots[0]]?.owned) {
      this.wsSlots[0] = ownedWeapons[0]?.[0] ?? 'pistol';
    }

    const slotColors = ['#ff4444', '#4488ff', '#44ff44'];
    const slotLabels = ['Slot 1', 'Slot 2', 'Slot 3'];

    // Slot drop zones (top)
    let slotsHtml = '';
    for (let i = 0; i < 3; i++) {
      const wId = this.wsSlots[i];
      const name = wId && WEAPONS[wId] ? t(WEAPONS[wId].nameKey) : '空きスロット';
      const hasWeapon = wId && WEAPONS[wId];
      const bg = hasWeapon ? `${slotColors[i]}18` : 'rgba(255,255,255,0.02)';
      const borderStyle = hasWeapon ? `2px solid ${slotColors[i]}` : '2px dashed #555';
      slotsHtml += `
        <div class="ws-drop-zone" data-slot-idx="${i}"
          style="flex:1;min-width:130px;min-height:90px;border:${borderStyle};background:${bg};
            display:flex;flex-direction:column;align-items:center;justify-content:center;
            transition:all 0.2s;border-radius:6px;position:relative;">
          <div style="font-size:0.7rem;color:${slotColors[i]};margin-bottom:4px;letter-spacing:0.1em;">${slotLabels[i]} [${i + 1}]</div>
          <div style="font-weight:bold;font-size:0.9rem;color:${hasWeapon ? '#fff' : '#555'};">${name}</div>
          ${hasWeapon ? `<button data-action="ws-remove" data-slot="${i}"
            style="position:absolute;top:4px;right:6px;background:none;border:none;color:#888;cursor:pointer;font-size:0.8rem;">✕</button>` : ''}
        </div>
      `;
    }

    // Draggable weapon cards
    const cards = ownedWeapons.map(([id, data]) => {
      const config = WEAPONS[id];
      const inSlot = this.wsSlots.indexOf(id);
      const slotColor = inSlot >= 0 ? slotColors[inSlot] : '#555';
      const bg = inSlot >= 0 ? `${slotColor}15` : 'rgba(255,255,255,0.04)';
      const opacity = inSlot >= 0 ? '0.5' : '1';
      const slotBadge = inSlot >= 0 ? `<span style="background:${slotColor};color:#000;font-size:0.6rem;padding:1px 6px;border-radius:3px;font-weight:bold;">S${inSlot + 1}</span>` : '';
      return `
        <div class="ws-weapon-card" draggable="true" data-weapon-id="${id}"
          style="width:140px;padding:10px;border:1px solid ${slotColor};background:${bg};
            cursor:grab;color:#fff;border-radius:6px;opacity:${opacity};
            transition:all 0.15s;user-select:none;-webkit-user-select:none;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
            <span style="font-weight:bold;font-size:0.9rem;">${t(config.nameKey)}</span>
            ${slotBadge}
          </div>
          <div style="color:#ffd700;font-size:0.8rem;margin-bottom:4px;">${t('weapon.level')}${data.level}</div>
          <div style="color:#999;font-size:0.7rem;">DMG: ${config.damage} | MAG: ${config.magazineSize}</div>
        </div>
      `;
    }).join('');

    this.clearScreen();
    this.setScreen(`
      <div style="width:100%;height:100%;background:rgba(0,0,0,0.95);display:flex;flex-direction:column;align-items:center;padding:2rem;overflow-y:auto;">
        <div style="font-size:2rem;color:#ff4444;margin-bottom:0.5rem;letter-spacing:0.2em;">${t('weapon.select')}</div>
        <div style="color:#888;font-size:0.85rem;margin-bottom:1.2rem;">武器をドラッグしてスロットに入れてください</div>
        <!-- Drop Slots -->
        <div style="display:flex;gap:10px;margin-bottom:1.5rem;max-width:520px;width:100%;">
          ${slotsHtml}
        </div>
        <!-- Arrow hint -->
        <div style="color:#555;font-size:1.2rem;margin-bottom:0.8rem;">↑ ドラッグして入れる ↑</div>
        <!-- Weapon Cards -->
        <div style="display:flex;flex-wrap:wrap;gap:10px;justify-content:center;max-width:800px;">
          ${cards}
        </div>
        <div style="display:flex;gap:1rem;margin-top:2rem;">
          <button data-action="ws-back" style="padding:12px 40px;font-size:1.1rem;font-weight:bold;cursor:pointer;border:2px solid #555;background:rgba(255,255,255,0.05);color:#aaa;border-radius:4px;">
            ${t('back')}
          </button>
          <button data-action="ws-confirm" style="padding:12px 40px;font-size:1.1rem;font-weight:bold;cursor:pointer;border:2px solid #ff0000;background:rgba(255,0,0,0.2);color:#ff4444;border-radius:4px;">
            ${t('weapon.confirm')}
          </button>
        </div>
      </div>
    `);

    // Setup drag & drop after DOM is ready
    requestAnimationFrame(() => this.setupWeaponDragDrop());
  }

  private setupWeaponDragDrop(): void {
    const container = this.overlay;
    if (!container) return;

    // Drag start on weapon cards
    container.querySelectorAll('.ws-weapon-card').forEach(card => {
      card.addEventListener('dragstart', ((e: DragEvent) => {
        const wId = (card as HTMLElement).getAttribute('data-weapon-id');
        if (!wId) return;
        e.dataTransfer!.setData('text/plain', wId);
        e.dataTransfer!.effectAllowed = 'move';
        (card as HTMLElement).style.opacity = '0.3';
      }) as EventListener);

      card.addEventListener('dragend', (() => {
        (card as HTMLElement).style.opacity = '';
      }) as EventListener);

      // Also support click to assign to first empty slot
      card.addEventListener('click', (() => {
        const wId = (card as HTMLElement).getAttribute('data-weapon-id');
        if (!wId) return;
        // Already in a slot? Remove it
        const existing = this.wsSlots.indexOf(wId);
        if (existing >= 0) {
          this.wsSlots[existing] = '';
          this.showWeaponSelect();
          return;
        }
        // Find first empty slot
        const emptyIdx = this.wsSlots.indexOf('');
        if (emptyIdx >= 0) {
          this.wsSlots[emptyIdx] = wId;
        } else {
          // All slots full — replace slot 3
          this.wsSlots[2] = wId;
        }
        this.showWeaponSelect();
      }) as EventListener);
    });

    // Drop zones
    container.querySelectorAll('.ws-drop-zone').forEach(zone => {
      zone.addEventListener('dragover', ((e: DragEvent) => {
        e.preventDefault();
        e.dataTransfer!.dropEffect = 'move';
        (zone as HTMLElement).style.borderStyle = 'solid';
        (zone as HTMLElement).style.borderColor = '#fff';
        (zone as HTMLElement).style.background = 'rgba(255,255,255,0.08)';
      }) as EventListener);

      zone.addEventListener('dragleave', (() => {
        (zone as HTMLElement).style.borderStyle = '';
        (zone as HTMLElement).style.borderColor = '';
        (zone as HTMLElement).style.background = '';
      }) as EventListener);

      zone.addEventListener('drop', ((e: DragEvent) => {
        e.preventDefault();
        const wId = e.dataTransfer!.getData('text/plain');
        const slotIdx = parseInt((zone as HTMLElement).getAttribute('data-slot-idx') ?? '0');
        if (!wId) return;

        // Remove from previous slot if already assigned
        const prevSlot = this.wsSlots.indexOf(wId);
        if (prevSlot >= 0) {
          // Swap with current slot content
          this.wsSlots[prevSlot] = this.wsSlots[slotIdx];
        }
        this.wsSlots[slotIdx] = wId;
        this.showWeaponSelect();
      }) as EventListener);
    });
  }

  // ==================== STAGE SELECT ====================
  private showStageSelect(): void {
    // Only render unlocked stages + first locked one (not all 10)
    const stageCards: string[] = [];
    for (const stage of STAGES) {
      const unlocked = this.saveManager.isStageUnlocked(stage.id);
      const cleared = this.saveManager.getLevelsCleared(stage.id);

      let levels = '';
      for (let i = 1; i <= 10; i++) {
        const isCleared = i <= cleared;
        const isPlayable = unlocked && i <= cleared + 1;

        if (isCleared) {
          levels += `<button data-action="play-level" data-stage="${stage.id}" data-level="${i}"
            style="display:flex;align-items:center;justify-content:center;border:1px solid #44ff44;color:#44ff44;background:rgba(0,255,0,0.2);padding:8px;cursor:pointer;font-family:inherit;">${i}</button>`;
        } else if (isPlayable) {
          levels += `<button data-action="play-level" data-stage="${stage.id}" data-level="${i}"
            style="display:flex;align-items:center;justify-content:center;border:1px solid #ff4444;color:#ff4444;background:rgba(255,0,0,0.15);padding:8px;cursor:pointer;font-family:inherit;">${i}</button>`;
        } else {
          levels += `<div style="display:flex;align-items:center;justify-content:center;border:1px solid #333;color:#333;padding:8px;opacity:0.4;">${i}</div>`;
        }
      }

      stageCards.push(`
        <div style="width:280px;padding:15px;border:1px solid #444;background:rgba(255,255,255,0.03);opacity:${unlocked ? '1' : '0.3'};">
          <div style="display:flex;justify-content:space-between;margin-bottom:10px;">
            <span style="color:#ff4444;font-weight:bold;">${t(stage.nameKey)}</span>
            <span style="color:#888;">${cleared}/10</span>
          </div>
          <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:5px;">${levels}</div>
          ${!unlocked ? `<div style="color:#ff0000;text-align:center;margin-top:8px;">${t('stage.locked')}</div>` : ''}
        </div>
      `);

      // Stop after first locked stage (no need to render rest)
      if (!unlocked) break;
    }

    this.clearScreen();
    this.setScreen(`
      <div style="width:100%;height:100%;background:rgba(0,0,0,0.95);display:flex;flex-direction:column;align-items:center;padding:1.5rem;overflow-y:auto;">
        <div style="font-size:2rem;color:#ff4444;margin-bottom:1.5rem;letter-spacing:0.2em;">${t('stage.select')}</div>
        <div style="display:flex;flex-wrap:wrap;gap:1rem;justify-content:center;max-width:900px;">
          ${stageCards.join('')}
        </div>
        <button data-action="ss-back" style="margin-top:1.5rem;padding:10px 30px;background:rgba(255,255,255,0.05);border:2px solid #555;color:#aaa;cursor:pointer;font-size:1rem;">
          ${t('back')}
        </button>
      </div>
    `);
  }

  // ==================== HUD ====================
  showHUD(): void {
    const container = this.setScreen(`
      <div style="width:100%;height:100%;pointer-events:none;position:relative;font-family:monospace;">
        <!-- Damage overlay -->
        <div id="damage-overlay" style="position:absolute;inset:0;border:4px solid transparent;pointer-events:none;transition:border-color 0.15s;"></div>

        <!-- Crosshair container (content set dynamically per weapon) -->
        <div id="crosshair-container" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);pointer-events:none;"></div>

        <!-- TOP LEFT: Coins -->
        <div id="hud-coins" style="position:absolute;top:8px;left:170px;color:#ffd700;font-size:1rem;font-weight:bold;text-shadow:0 0 6px rgba(0,0,0,0.9);white-space:nowrap;">${t('shop.coins')}: 0</div>

        <!-- TOP RIGHT: Zombies remaining + Pause -->
        <div style="position:absolute;top:12px;right:15px;display:flex;align-items:center;gap:12px;">
          <div id="hud-zombies" style="color:#ff4444;font-size:1.1rem;font-weight:bold;text-shadow:0 0 6px rgba(0,0,0,0.9);">${t('hud.remaining')}: 0</div>
          <button data-action="pause-game" style="background:rgba(255,255,255,0.15);border:2px solid rgba(255,255,255,0.4);color:#fff;padding:12px 18px;cursor:pointer;pointer-events:auto;font-size:1.4rem;font-weight:bold;border-radius:6px;min-width:50px;min-height:50px;touch-action:manipulation;">||</button>
        </div>

        <!-- LEFT SIDE: Minimap + HP + Light -->
        <div style="position:absolute;top:45px;left:15px;">
          <canvas id="minimap" width="150" height="150" style="border:1px solid rgba(100,200,100,0.4);background:rgba(0,0,0,0.7);display:block;"></canvas>
          <!-- HP below minimap -->
          <div style="margin-top:8px;">
            <div id="hp-text" style="color:#fff;font-size:1rem;font-weight:bold;text-shadow:0 0 6px rgba(0,0,0,0.9);margin-bottom:3px;">HP: 100/100</div>
            <div style="width:150px;height:14px;background:rgba(0,0,0,0.6);border:2px solid rgba(255,255,255,0.3);border-radius:2px;">
              <div id="hp-bar" style="height:100%;background:linear-gradient(180deg,#66ff66,#22cc22);width:100%;transition:width 0.15s;border-radius:1px;"></div>
            </div>
          </div>
          <!-- Armor bar -->
          <div id="armor-container" style="margin-top:5px;display:none;">
            <div id="armor-text" style="color:#44ddff;font-size:0.85rem;font-weight:bold;text-shadow:0 0 6px rgba(0,0,0,0.9);margin-bottom:2px;">ARMOR: 0/50</div>
            <div style="width:150px;height:10px;background:rgba(0,0,0,0.6);border:2px solid rgba(68,220,255,0.4);border-radius:2px;">
              <div id="armor-bar" style="height:100%;background:linear-gradient(180deg,#66eeff,#2299dd);width:0%;transition:width 0.15s;border-radius:1px;"></div>
            </div>
          </div>
          <!-- Energy bar -->
          <div style="margin-top:6px;">
            <div id="energy-text" style="color:#44ccff;font-size:0.85rem;font-weight:bold;text-shadow:0 0 6px rgba(0,0,0,0.9);margin-bottom:2px;">ENERGY: 100%</div>
            <div style="width:150px;height:10px;background:rgba(0,0,0,0.6);border:2px solid rgba(68,204,255,0.3);border-radius:2px;">
              <div id="energy-bar" style="height:100%;background:linear-gradient(180deg,#66ddff,#2299cc);width:100%;transition:width 0.1s;border-radius:1px;"></div>
            </div>
          </div>
          <!-- Flashlight status -->
          <div id="hud-flashlight" style="color:#ffaa00;font-size:0.9rem;margin-top:6px;text-shadow:0 0 6px rgba(0,0,0,0.9);">LIGHT: ON</div>
        </div>

        <!-- BOTTOM RIGHT: Weapon panel -->
        <div style="position:absolute;bottom:15px;right:15px;display:flex;flex-direction:column;align-items:flex-end;gap:4px;">
          <!-- Reload bar -->
          <div id="reload-container" style="display:none;background:rgba(0,0,0,0.8);border:2px solid #ffaa00;padding:6px 12px;width:240px;">
            <div style="color:#ffaa00;font-size:0.9rem;font-weight:bold;margin-bottom:4px;">${t('hud.reloading')}</div>
            <div style="width:100%;height:6px;background:rgba(255,255,255,0.1);border-radius:3px;">
              <div id="reload-bar" style="height:100%;background:linear-gradient(90deg,#ff8800,#ffcc00);width:0%;border-radius:3px;transition:width 0.05s;"></div>
            </div>
          </div>
          <!-- Weapon card -->
          <div style="background:rgba(0,0,0,0.8);border:2px solid rgba(255,255,255,0.3);padding:10px;width:240px;">
            <!-- Weapon drawing -->
            <canvas id="weapon-canvas" width="220" height="50" style="display:block;margin-bottom:6px;"></canvas>
            <!-- Weapon name -->
            <div id="weapon-name" style="color:#ccc;font-size:0.9rem;font-weight:bold;margin-bottom:6px;">Pistol Lv.1</div>
            <!-- Ammo row -->
            <div style="display:flex;align-items:center;justify-content:space-between;">
              <div style="display:flex;align-items:baseline;gap:4px;">
                <span id="weapon-ammo" style="color:#fff;font-size:2.2rem;font-weight:bold;line-height:1;">12</span>
                <span id="weapon-ammo-max" style="color:#666;font-size:1rem;">/ 12</span>
              </div>
              <div id="reload-hint" style="color:#666;font-size:0.75rem;">[R] Reload</div>
            </div>
            <!-- Ammo bullets visual -->
            <div id="ammo-visual" style="display:flex;gap:2px;flex-wrap:wrap;margin-top:8px;"></div>
          </div>
        </div>
      </div>
    `);
    this.hudElement = container;
    this.initMinimap();
    this.lastWeaponDrawn = '';

    // Draw initial weapon
    this.drawWeaponIcon('Pistol');
  }

  private minimapCtx: CanvasRenderingContext2D | null = null;
  private lastWeaponDrawn = '';

  private drawWeaponIcon(weaponName: string): void {
    if (!this.hudElement) return;
    const canvas = this.hudElement.querySelector('#weapon-canvas') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, 220, 50);

    ctx.strokeStyle = '#aaaaaa';
    ctx.fillStyle = '#888888';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const cx = 110, cy = 25;

    switch (weaponName) {
      case 'Pistol': {
        // Slide (top)
        ctx.fillStyle = '#4a4a4a';
        ctx.fillRect(cx - 8, cy - 10, 58, 8);
        // Slide serrations
        ctx.fillStyle = '#3a3a3a';
        for (let i = 0; i < 6; i++) ctx.fillRect(cx + 32 + i * 3, cy - 10, 1, 8);
        // Barrel
        ctx.fillStyle = '#555';
        ctx.fillRect(cx + 48, cy - 7, 12, 5);
        // Muzzle
        ctx.fillStyle = '#333';
        ctx.fillRect(cx + 58, cy - 8, 4, 7);
        // Frame (lower)
        ctx.fillStyle = '#5a5a5a';
        ctx.fillRect(cx - 12, cy - 2, 48, 8);
        // Rail
        ctx.fillStyle = '#666';
        ctx.fillRect(cx - 5, cy - 3, 30, 2);
        // Trigger guard
        ctx.strokeStyle = '#555';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(cx + 5, cy + 6);
        ctx.quadraticCurveTo(cx + 10, cy + 16, cx + 18, cy + 6);
        ctx.stroke();
        // Trigger
        ctx.fillStyle = '#333';
        ctx.fillRect(cx + 10, cy + 4, 3, 8);
        // Grip (textured)
        ctx.fillStyle = '#2a2a2a';
        ctx.fillRect(cx - 14, cy + 4, 16, 22);
        ctx.fillStyle = '#222';
        for (let i = 0; i < 4; i++) ctx.fillRect(cx - 12, cy + 8 + i * 5, 12, 1);
        // Magazine
        ctx.fillStyle = '#383838';
        ctx.fillRect(cx - 11, cy + 20, 10, 8);
        // Baseplate
        ctx.fillStyle = '#444';
        ctx.fillRect(cx - 12, cy + 26, 12, 2);
        // Sights
        ctx.fillStyle = '#bbb';
        ctx.fillRect(cx + 56, cy - 13, 2, 4);
        ctx.fillRect(cx + 36, cy - 12, 2, 3);
        ctx.fillRect(cx + 42, cy - 12, 2, 3);
        // Ejection port
        ctx.fillStyle = '#666';
        ctx.fillRect(cx + 20, cy - 9, 8, 4);
        break;
      }
      case 'Shotgun': {
        // Barrel
        ctx.fillStyle = '#4a4a4a';
        ctx.fillRect(cx - 30, cy - 6, 95, 7);
        // Magazine tube (under barrel)
        ctx.fillStyle = '#555';
        ctx.fillRect(cx - 25, cy + 1, 80, 5);
        // Muzzle
        ctx.fillStyle = '#333';
        ctx.fillRect(cx + 63, cy - 7, 4, 9);
        // Receiver
        ctx.fillStyle = '#3a3a3a';
        ctx.fillRect(cx - 35, cy - 8, 30, 16);
        // Ejection port
        ctx.fillStyle = '#555';
        ctx.fillRect(cx - 22, cy - 6, 10, 5);
        // Pump/forend (wood)
        ctx.fillStyle = '#7a5a30';
        ctx.fillRect(cx + 5, cy - 4, 28, 12);
        // Wood grain lines
        ctx.strokeStyle = '#6a4a20';
        ctx.lineWidth = 0.5;
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.moveTo(cx + 6, cy + i * 4);
          ctx.lineTo(cx + 32, cy - 1 + i * 4);
          ctx.stroke();
        }
        // Stock (wood)
        ctx.fillStyle = '#6b4a28';
        ctx.beginPath();
        ctx.moveTo(cx - 35, cy - 7);
        ctx.lineTo(cx - 75, cy + 2);
        ctx.lineTo(cx - 78, cy + 8);
        ctx.lineTo(cx - 75, cy + 14);
        ctx.lineTo(cx - 35, cy + 10);
        ctx.fill();
        // Stock butt pad
        ctx.fillStyle = '#333';
        ctx.fillRect(cx - 78, cy + 2, 4, 12);
        // Grip area
        ctx.fillStyle = '#5a3a18';
        ctx.fillRect(cx - 38, cy + 4, 14, 18);
        // Trigger guard
        ctx.strokeStyle = '#444';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(cx - 30, cy + 8);
        ctx.quadraticCurveTo(cx - 25, cy + 18, cx - 18, cy + 8);
        ctx.stroke();
        // Trigger
        ctx.fillStyle = '#333';
        ctx.fillRect(cx - 25, cy + 7, 2, 7);
        // Front sight bead
        ctx.fillStyle = '#ff6644';
        ctx.beginPath();
        ctx.arc(cx + 63, cy - 9, 2, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 'SMG': {
        // Upper receiver
        ctx.fillStyle = '#3a3a3a';
        ctx.fillRect(cx - 20, cy - 9, 50, 12);
        // Barrel + shroud
        ctx.fillStyle = '#444';
        ctx.fillRect(cx + 28, cy - 6, 32, 8);
        // Barrel holes
        ctx.fillStyle = '#333';
        for (let i = 0; i < 4; i++) ctx.fillRect(cx + 32 + i * 7, cy - 4, 3, 4);
        // Muzzle
        ctx.fillStyle = '#2a2a2a';
        ctx.fillRect(cx + 58, cy - 7, 5, 10);
        // Rail (top)
        ctx.fillStyle = '#555';
        ctx.fillRect(cx - 15, cy - 11, 42, 3);
        for (let i = 0; i < 8; i++) ctx.fillRect(cx - 14 + i * 5, cy - 12, 3, 1);
        // Lower receiver
        ctx.fillStyle = '#444';
        ctx.fillRect(cx - 20, cy + 3, 35, 5);
        // Magazine (curved)
        ctx.fillStyle = '#333';
        ctx.beginPath();
        ctx.moveTo(cx - 2, cy + 8);
        ctx.lineTo(cx + 10, cy + 8);
        ctx.lineTo(cx + 8, cy + 28);
        ctx.lineTo(cx, cy + 28);
        ctx.fill();
        // Stock (retractable)
        ctx.fillStyle = '#444';
        ctx.fillRect(cx - 48, cy - 5, 30, 4);
        ctx.fillRect(cx - 48, cy + 3, 30, 4);
        ctx.fillStyle = '#555';
        ctx.fillRect(cx - 52, cy - 3, 6, 10);
        // Grip
        ctx.fillStyle = '#2a2a2a';
        ctx.fillRect(cx - 18, cy + 6, 10, 16);
        ctx.fillStyle = '#222';
        for (let i = 0; i < 3; i++) ctx.fillRect(cx - 16, cy + 9 + i * 4, 6, 1);
        // Foregrip
        ctx.fillStyle = '#333';
        ctx.fillRect(cx + 12, cy + 6, 8, 12);
        // Trigger
        ctx.fillStyle = '#333';
        ctx.fillRect(cx - 8, cy + 5, 2, 6);
        break;
      }
      case 'Rocket Launcher': {
        // Main launch tube
        const tubeGrad = ctx.createLinearGradient(cx - 50, cy - 12, cx - 50, cy + 10);
        tubeGrad.addColorStop(0, '#4a6a4a');
        tubeGrad.addColorStop(0.5, '#3a5a3a');
        tubeGrad.addColorStop(1, '#2a4a2a');
        ctx.fillStyle = tubeGrad;
        ctx.beginPath();
        ctx.roundRect(cx - 52, cy - 12, 105, 22, 4);
        ctx.fill();
        // Tube rings/bands
        ctx.strokeStyle = '#2a3a2a';
        ctx.lineWidth = 1;
        for (const rx of [-40, -15, 10, 35]) {
          ctx.beginPath();
          ctx.moveTo(cx + rx, cy - 12);
          ctx.lineTo(cx + rx, cy + 10);
          ctx.stroke();
        }
        // Front opening (dark circle)
        ctx.fillStyle = '#1a1a1a';
        ctx.beginPath();
        ctx.ellipse(cx + 53, cy - 1, 10, 10, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#555';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        // Rocket visible inside tube
        ctx.fillStyle = '#6b5a3a';
        ctx.beginPath();
        ctx.ellipse(cx + 53, cy - 1, 6, 6, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#8b4513';
        ctx.beginPath();
        ctx.ellipse(cx + 53, cy - 1, 3, 3, 0, 0, Math.PI * 2);
        ctx.fill();
        // Rear exhaust opening
        ctx.fillStyle = '#222';
        ctx.beginPath();
        ctx.ellipse(cx - 52, cy - 1, 8, 8, 0, 0, Math.PI * 2);
        ctx.fill();
        // Grip + trigger assembly
        ctx.fillStyle = '#333';
        ctx.fillRect(cx - 18, cy + 10, 12, 18);
        ctx.fillStyle = '#2a2a2a';
        for (let i = 0; i < 3; i++) ctx.fillRect(cx - 16, cy + 13 + i * 5, 8, 1);
        // Trigger guard
        ctx.strokeStyle = '#444';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(cx - 10, cy + 10);
        ctx.quadraticCurveTo(cx - 5, cy + 22, cx, cy + 10);
        ctx.stroke();
        // Optical sight
        ctx.fillStyle = '#3a3a3a';
        ctx.fillRect(cx - 15, cy - 20, 35, 9);
        ctx.fillStyle = '#444';
        ctx.fillRect(cx - 13, cy - 18, 31, 5);
        // Sight lens
        ctx.fillStyle = '#3366aa';
        ctx.beginPath();
        ctx.arc(cx - 13, cy - 15, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#4488cc';
        ctx.beginPath();
        ctx.arc(cx + 20, cy - 15, 3, 0, Math.PI * 2);
        ctx.fill();
        // Shoulder rest
        ctx.fillStyle = '#444';
        ctx.fillRect(cx - 55, cy + 2, 8, 12);
        break;
      }
      case 'Assault Rifle': {
        // Upper receiver + barrel
        ctx.fillStyle = '#3a3a3a';
        ctx.fillRect(cx - 25, cy - 8, 50, 12);
        // Barrel + handguard
        ctx.fillStyle = '#444';
        ctx.fillRect(cx + 23, cy - 6, 38, 8);
        // Muzzle brake
        ctx.fillStyle = '#333';
        ctx.fillRect(cx + 59, cy - 7, 6, 10);
        // Rail (top)
        ctx.fillStyle = '#555';
        ctx.fillRect(cx - 20, cy - 10, 48, 3);
        for (let i = 0; i < 9; i++) ctx.fillRect(cx - 19 + i * 5, cy - 11, 3, 1);
        // Stock
        ctx.fillStyle = '#444';
        ctx.fillRect(cx - 50, cy - 5, 27, 6);
        ctx.fillStyle = '#333';
        ctx.fillRect(cx - 55, cy - 4, 7, 8);
        // Magazine (curved)
        ctx.fillStyle = '#333';
        ctx.beginPath();
        ctx.moveTo(cx - 5, cy + 4);
        ctx.lineTo(cx + 5, cy + 4);
        ctx.lineTo(cx + 3, cy + 26);
        ctx.lineTo(cx - 3, cy + 26);
        ctx.fill();
        // Grip
        ctx.fillStyle = '#2a2a2a';
        ctx.fillRect(cx - 22, cy + 4, 10, 16);
        // Trigger guard
        ctx.strokeStyle = '#444';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(cx - 14, cy + 4);
        ctx.quadraticCurveTo(cx - 10, cy + 16, cx - 4, cy + 4);
        ctx.stroke();
        // Sights
        ctx.fillStyle = '#bbb';
        ctx.fillRect(cx + 55, cy - 13, 2, 4);
        break;
      }
      case 'Grenade': {
        // Body
        ctx.fillStyle = '#4a6b4a';
        ctx.beginPath();
        ctx.ellipse(cx, cy, 14, 18, 0, 0, Math.PI * 2);
        ctx.fill();
        // Lines
        ctx.strokeStyle = '#3a5a3a';
        ctx.lineWidth = 1;
        for (let i = -2; i <= 2; i++) {
          ctx.beginPath();
          ctx.moveTo(cx - 12, cy + i * 6);
          ctx.lineTo(cx + 12, cy + i * 6);
          ctx.stroke();
        }
        // Top cap
        ctx.fillStyle = '#555';
        ctx.fillRect(cx - 5, cy - 20, 10, 5);
        // Lever
        ctx.fillStyle = '#888';
        ctx.fillRect(cx + 3, cy - 22, 3, 15);
        // Pin
        ctx.strokeStyle = '#aaa';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(cx - 6, cy - 22, 4, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
    }
  }

  private initMinimap(): void {
    const canvas = this.hudElement?.querySelector('#minimap') as HTMLCanvasElement;
    if (!canvas) return;
    this.minimapCtx = canvas.getContext('2d');
  }

  private drawMinimap(playerX: number, playerZ: number, playerRotY: number, exitDoorX?: number, exitDoorZ?: number, stageId = 1): void {
    const ctx = this.minimapCtx;
    if (!ctx) return;

    const W = 150;
    const H = 150;
    const scale = 4.5; // pixels per meter
    const cx = W / 2;
    const cy = H / 2;

    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = 'rgba(0,0,0,0.8)';
    ctx.fillRect(0, 0, W, H);

    // Map offset: center on player
    const ox = cx - playerX * scale;
    const oy = cy - playerZ * scale;

    // Helper to draw wall
    ctx.strokeStyle = 'rgba(100,150,100,0.6)';
    ctx.lineWidth = 2;
    const wall = (x1: number, z1: number, x2: number, z2: number) => {
      ctx.beginPath();
      ctx.moveTo(x1 * scale + ox, z1 * scale + oy);
      ctx.lineTo(x2 * scale + ox, z2 * scale + oy);
      ctx.stroke();
    };
    // Helper to draw filled rect
    const fillRect = (x: number, z: number, w: number, h: number, color: string) => {
      ctx.fillStyle = color;
      ctx.fillRect(x * scale + ox, z * scale + oy, w * scale, h * scale);
    };

    if (stageId === 1) {
      // === STAGE 1: Laboratory (3-room layout) ===
      wall(-15, -15, 15, -15);
      wall(-15, 15, 15, 15);
      wall(-15, -15, -15, 15);
      wall(15, -15, 15, 15);
      wall(0, -15, 0, -9);
      wall(0, -7, 0, -2);
      wall(-15, -2, -11, -2);
      wall(-7, -2, 1, -2);
      wall(1, -2, 7, -2);
      wall(11, -2, 15, -2);
      wall(-15, 2, -4, 2);
      wall(4, 2, 15, 2);

      ctx.fillStyle = 'rgba(100,150,100,0.4)';
      ctx.font = '8px monospace';
      ctx.fillText('Lab A', (-7.5) * scale + ox, (-8) * scale + oy);
      ctx.fillText('Lab B', (3) * scale + ox, (-8) * scale + oy);
      ctx.fillText('Storage', (-2) * scale + ox, (8) * scale + oy);
    } else if (stageId === 2) {
      // === STAGE 2: City / Shopping District ===
      // Road (dark strip in center)
      fillRect(-5, -25, 10, 50, 'rgba(40,40,40,0.5)');
      // Center line
      ctx.strokeStyle = 'rgba(200,200,80,0.4)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      wall(0, -24, 0, 24);
      ctx.setLineDash([]);

      // Sidewalks
      fillRect(-10, -25, 5, 50, 'rgba(80,80,80,0.3)');
      fillRect(5, -25, 5, 50, 'rgba(80,80,80,0.3)');

      // Buildings (solid blocks along sides)
      ctx.strokeStyle = 'rgba(120,140,160,0.6)';
      ctx.lineWidth = 1;
      for (const side of [-1, 1]) {
        for (let i = 0; i < 6; i++) {
          const bx = side > 0 ? 9.5 : -14.5;
          const bz = -20 + i * 7.5 - 3;
          fillRect(bx, bz, 5, 6, 'rgba(70,80,90,0.4)');
          ctx.strokeRect(bx * scale + ox, bz * scale + oy, 5 * scale, 6 * scale);
        }
      }

      // Cars (small colored rectangles)
      const carSpots: [number, number, string][] = [
        [-2, -8, 'rgba(150,50,50,0.5)'], [3, 5, 'rgba(50,50,150,0.5)'],
        [-1, 15, 'rgba(100,100,100,0.5)'], [2, -15, 'rgba(50,150,50,0.5)'],
      ];
      for (const [cx, cz, col] of carSpots) {
        fillRect(cx - 0.7, cz - 1.5, 1.4, 3, col);
      }

      // Labels
      ctx.fillStyle = 'rgba(150,170,190,0.5)';
      ctx.font = '7px monospace';
      ctx.fillText('商店街', (-3) * scale + ox, (-22) * scale + oy);

    } else if (stageId === 3) {
      // === STAGE 3: Hospital ===
      const s = 2.8; // smaller scale for 50x50 map
      const oxx = cx - playerX * s;
      const oyy = cy - playerZ * s;
      const hw = (x1: number, z1: number, x2: number, z2: number) => {
        ctx.beginPath();
        ctx.moveTo(x1 * s + oxx, z1 * s + oyy);
        ctx.lineTo(x2 * s + oxx, z2 * s + oyy);
        ctx.stroke();
      };
      const hf = (x: number, z: number, w: number, h: number, color: string) => {
        ctx.fillStyle = color;
        ctx.fillRect(x * s + oxx, z * s + oyy, w * s, h * s);
      };

      ctx.strokeStyle = 'rgba(100,150,100,0.6)';
      ctx.lineWidth = 1.5;

      // Outer walls
      hw(-25, -25, 25, -25); hw(-25, 25, 25, 25);
      hw(-25, -25, -25, 25); hw(25, -25, 25, 25);

      // Main corridor (center, north-south)
      hw(-3, -6, -3, 16); hw(3, -6, 3, 16);

      // East wing corridor
      hw(3, -3, 21, -3); hw(3, 3, 21, 3);
      // West wing corridor
      hw(-3, -3, -21, -3); hw(-3, 3, -21, 3);

      // East rooms outer walls
      hw(3, -13, 21, -13); hw(3, 13, 21, 13);
      // West rooms outer walls
      hw(-3, -13, -21, -13); hw(-3, 13, -21, 13);

      // Room dividers east
      for (let i = 0; i < 3; i++) {
        hw(6 + i * 5, -13, 6 + i * 5, -3);
        hw(6 + i * 5, 3, 6 + i * 5, 13);
      }
      // Room dividers west
      for (let i = 0; i < 3; i++) {
        hw(-6 - i * 5, -13, -6 - i * 5, -3);
        hw(-6 - i * 5, 3, -6 - i * 5, 13);
      }

      // North storage wall
      hw(-3, -16, 3, -16);

      // Reception desk
      hf(-3, 17, 6, 1, 'rgba(150,150,200,0.4)');

      // Beds (small dots)
      ctx.fillStyle = 'rgba(200,200,255,0.3)';
      for (const bx of [5, 10, 15, 20, -5, -10, -15, -20]) {
        for (const bz of [-6, -9, 6, 9]) {
          ctx.fillRect(bx * s + oxx - 1, bz * s + oyy - 1, 3, 2);
        }
      }

      // Labels
      ctx.fillStyle = 'rgba(100,150,100,0.5)';
      ctx.font = '7px monospace';
      ctx.fillText('受付', (-1) * s + oxx, (20) * s + oyy);
      ctx.fillText('病室', (10) * s + oxx, (-8) * s + oyy);
      ctx.fillText('病室', (-14) * s + oxx, (-8) * s + oyy);

      // Player dot
      ctx.fillStyle = '#44ff44';
      ctx.beginPath();
      ctx.arc(cx, cy, 3, 0, Math.PI * 2);
      ctx.fill();
      // Direction arrow
      ctx.strokeStyle = '#44ff44';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.sin(playerRotY) * 8, cy + Math.cos(playerRotY) * 8);
      ctx.stroke();

      // Exit door marker
      if (exitDoorX !== undefined && exitDoorZ !== undefined) {
        const ex = exitDoorX * s + oxx;
        const ez = exitDoorZ * s + oyy;
        ctx.fillStyle = '#ff4444';
        ctx.fillRect(ex - 3, ez - 3, 6, 6);
      }

      // Border
      ctx.strokeStyle = 'rgba(100,200,100,0.4)';
      ctx.lineWidth = 1;
      ctx.strokeRect(0, 0, W, H);
      return; // skip default player/exit drawing below

    } else {
      // === DEFAULT: Simple rectangular arena ===
      wall(-15, -15, 15, -15);
      wall(-15, 15, 15, 15);
      wall(-15, -15, -15, 15);
      wall(15, -15, 15, 15);
      ctx.fillStyle = 'rgba(100,150,100,0.4)';
      ctx.font = '8px monospace';
      ctx.fillText(`Stage ${stageId}`, (-3) * scale + ox, 0);
    }

    // Player position (green triangle, tip = facing direction)
    // playerRotY = atan2(dir.x, dir.z) from camera world direction
    // On minimap: -Z is up, +X is right
    // atan2(x,z): facing -Z → PI, facing +X → PI/2, facing +Z → 0, facing -X → -PI/2
    // Canvas rotation: 0 = up, positive = clockwise
    // We want: facing -Z (forward in 3D) → triangle points up (rotation = 0)
    // So: canvasRot = -(playerRotY - PI) = PI - playerRotY
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(Math.PI - playerRotY);
    ctx.fillStyle = '#44ff44';
    ctx.beginPath();
    ctx.moveTo(0, -7);   // tip (front)
    ctx.lineTo(-5, 6);
    ctx.lineTo(5, 6);
    ctx.closePath();
    ctx.fill();
    // Outline
    ctx.strokeStyle = '#88ff88';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // Exit door marker (pulsing yellow icon)
    if (exitDoorX !== undefined && exitDoorZ !== undefined) {
      const dx = exitDoorX * scale + ox;
      const dz = exitDoorZ * scale + oy;
      // Pulse effect
      const pulse = 0.6 + Math.sin(performance.now() * 0.005) * 0.4;
      const r = 5 + pulse * 2;
      // Outer glow
      ctx.fillStyle = `rgba(255,200,0,${pulse * 0.3})`;
      ctx.beginPath();
      ctx.arc(dx, dz, r + 3, 0, Math.PI * 2);
      ctx.fill();
      // Door icon (filled diamond)
      ctx.fillStyle = `rgba(255,220,0,${0.5 + pulse * 0.5})`;
      ctx.beginPath();
      ctx.moveTo(dx, dz - r);
      ctx.lineTo(dx + r * 0.7, dz);
      ctx.lineTo(dx, dz + r);
      ctx.lineTo(dx - r * 0.7, dz);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#ffcc00';
      ctx.lineWidth = 1;
      ctx.stroke();
      // "EXIT" text
      ctx.fillStyle = '#ffcc00';
      ctx.font = 'bold 7px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('EXIT', dx, dz - r - 3);
      ctx.textAlign = 'start';
    }

    // Border
    ctx.strokeStyle = 'rgba(100,200,100,0.4)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, W, H);
  }

  updateHUD(data: {
    hp: number; maxHp: number;
    weaponName: string; weaponLevel: number;
    currentAmmo: number; maxAmmo: number;
    isReloading: boolean; reloadProgress?: number;
    coins: number;
    zombiesRemaining: number; flashlightOn: boolean;
    energy?: number; maxEnergy?: number; energyDepleted?: boolean;
    armor?: number; maxArmor?: number;
    isADS?: boolean;
    playerX?: number; playerZ?: number; playerRotY?: number;
    exitDoorX?: number; exitDoorZ?: number;
    stageId?: number;
  }): void {
    if (!this.hudElement) return;
    const $ = (id: string) => this.hudElement!.querySelector('#' + id) as HTMLElement | null;

    const hpPercent = (data.hp / data.maxHp) * 100;
    const hpBar = $('hp-bar');
    if (hpBar) {
      hpBar.style.width = `${hpPercent}%`;
      hpBar.style.background = hpPercent > 50
        ? 'linear-gradient(180deg,#66ff66,#22cc22)'
        : hpPercent > 25
          ? 'linear-gradient(180deg,#ffcc44,#cc8800)'
          : 'linear-gradient(180deg,#ff6644,#cc2200)';
    }
    const hpText = $('hp-text');
    if (hpText) hpText.textContent = `HP: ${Math.ceil(data.hp)}/${data.maxHp}`;

    // Weapon name & level
    const wn = $('weapon-name');
    if (wn) wn.textContent = `${data.weaponName} ${t('weapon.level')}${data.weaponLevel}`;

    // Redraw weapon icon if changed
    if (data.weaponName !== this.lastWeaponDrawn) {
      this.lastWeaponDrawn = data.weaponName;
      this.drawWeaponIcon(data.weaponName);
    }

    // Ammo count
    const ammo = $('weapon-ammo');
    if (ammo) {
      ammo.textContent = `${data.currentAmmo}`;
      ammo.style.color = data.currentAmmo <= 3 && data.currentAmmo > 0 ? '#ff4444' : data.currentAmmo === 0 ? '#ff0000' : '#ffffff';
    }
    const ammoMax = $('weapon-ammo-max');
    if (ammoMax) ammoMax.textContent = `/ ${data.maxAmmo}`;

    // Ammo visual (individual bullets)
    const ammoVisual = $('ammo-visual');
    if (ammoVisual) {
      let bulletsHtml = '';
      for (let i = 0; i < data.maxAmmo; i++) {
        const loaded = i < data.currentAmmo;
        const color = loaded ? '#ffcc44' : 'rgba(255,255,255,0.15)';
        bulletsHtml += `<div style="width:4px;height:12px;background:${color};border-radius:1px;"></div>`;
      }
      ammoVisual.innerHTML = bulletsHtml;
    }

    // Reload indicator with progress bar
    const reloadContainer = $('reload-container');
    if (reloadContainer) {
      reloadContainer.style.display = data.isReloading ? 'block' : 'none';
    }
    const reloadBar = $('reload-bar');
    if (reloadBar && data.reloadProgress !== undefined) {
      reloadBar.style.width = `${data.reloadProgress * 100}%`;
    }
    // Reload hint
    const reloadHint = $('reload-hint');
    if (reloadHint) {
      if (data.isReloading) {
        reloadHint.style.display = 'none';
      } else if (data.currentAmmo < data.maxAmmo) {
        reloadHint.style.display = 'block';
        reloadHint.style.color = data.currentAmmo === 0 ? '#ff4444' : '#ffaa00';
        reloadHint.textContent = data.currentAmmo === 0 ? '[R] RELOAD!' : '[R] Reload';
      } else {
        reloadHint.style.color = '#444';
        reloadHint.textContent = '[R] Reload';
      }
    }

    const coins = $('hud-coins');
    if (coins) coins.textContent = `${t('shop.coins')}: ${data.coins}`;

    const zombies = $('hud-zombies');
    if (zombies) zombies.textContent = `${t('hud.remaining')}: ${data.zombiesRemaining}`;

    const fl = $('hud-flashlight');
    if (fl) fl.textContent = `LIGHT: ${data.flashlightOn ? 'ON' : 'OFF'}`;

    // Armor bar
    if (data.armor !== undefined && data.maxArmor !== undefined) {
      const armorContainer = $('armor-container');
      if (armorContainer) {
        armorContainer.style.display = data.maxArmor > 0 && data.armor > 0 ? 'block' : (data.maxArmor > 0 ? 'block' : 'none');
      }
      const armorPercent = data.maxArmor > 0 ? (data.armor / data.maxArmor) * 100 : 0;
      const armorBar = $('armor-bar');
      if (armorBar) armorBar.style.width = `${armorPercent}%`;
      const armorText = $('armor-text');
      if (armorText) armorText.textContent = `ARMOR: ${Math.ceil(data.armor)}/${data.maxArmor}`;
    }

    // Energy bar
    if (data.energy !== undefined && data.maxEnergy !== undefined) {
      const energyPercent = (data.energy / data.maxEnergy) * 100;
      const energyBar = $('energy-bar');
      if (energyBar) {
        energyBar.style.width = `${energyPercent}%`;
        if (data.energyDepleted) {
          energyBar.style.background = 'linear-gradient(180deg,#ff6644,#cc2200)';
        } else if (energyPercent < 30) {
          energyBar.style.background = 'linear-gradient(180deg,#ffcc44,#cc8800)';
        } else {
          energyBar.style.background = 'linear-gradient(180deg,#66ddff,#2299cc)';
        }
      }
      const energyText = $('energy-text');
      if (energyText) {
        energyText.textContent = `ENERGY: ${Math.ceil(energyPercent)}%`;
        energyText.style.color = data.energyDepleted ? '#ff4444' : energyPercent < 30 ? '#ffaa00' : '#44ccff';
      }
    }

    // Crosshair: per-weapon style + ADS variant
    const chContainer = $('crosshair-container');
    if (chContainer) {
      const wn = data.weaponName;
      const ads = data.isADS;
      const key = (ads ? 'ads:' : '') + wn;
      if (chContainer.getAttribute('data-ch') !== key) {
        chContainer.setAttribute('data-ch', key);
        if (ads) {
          // ADS: tight red dot for all weapons
          chContainer.innerHTML = `
            <div style="width:1px;height:8px;background:#f44;opacity:0.9;position:absolute;left:0px;top:-10px;"></div>
            <div style="width:1px;height:8px;background:#f44;opacity:0.9;position:absolute;left:0px;top:3px;"></div>
            <div style="width:8px;height:1px;background:#f44;opacity:0.9;position:absolute;left:-10px;top:0px;"></div>
            <div style="width:8px;height:1px;background:#f44;opacity:0.9;position:absolute;left:3px;top:0px;"></div>
            <div style="width:3px;height:3px;background:#f44;border-radius:50%;position:absolute;left:-1px;top:-1px;"></div>`;
        } else if (wn === 'Shotgun') {
          // Shotgun: square spread reticle
          chContainer.innerHTML = `
            <div style="width:28px;height:28px;border:2px solid rgba(255,255,255,0.7);position:absolute;left:-14px;top:-14px;"></div>
            <div style="width:2px;height:2px;background:rgba(255,80,80,0.8);position:absolute;left:-1px;top:-1px;"></div>`;
        } else if (wn === 'SMG') {
          // SMG: circle reticle
          chContainer.innerHTML = `
            <div style="width:22px;height:22px;border:2px solid rgba(255,255,255,0.6);border-radius:50%;position:absolute;left:-11px;top:-11px;"></div>
            <div style="width:2px;height:6px;background:rgba(255,255,255,0.5);position:absolute;left:-1px;top:-14px;"></div>
            <div style="width:2px;height:6px;background:rgba(255,255,255,0.5);position:absolute;left:-1px;top:8px;"></div>
            <div style="width:6px;height:2px;background:rgba(255,255,255,0.5);position:absolute;left:-14px;top:-1px;"></div>
            <div style="width:6px;height:2px;background:rgba(255,255,255,0.5);position:absolute;left:8px;top:-1px;"></div>
            <div style="width:2px;height:2px;background:rgba(255,80,80,0.8);position:absolute;left:-1px;top:-1px;"></div>`;
        } else if (wn === 'Rocket Launcher') {
          // Rocket: large square with inner cross
          chContainer.innerHTML = `
            <div style="width:36px;height:36px;border:2px solid rgba(255,100,0,0.6);position:absolute;left:-18px;top:-18px;"></div>
            <div style="width:1px;height:14px;background:rgba(255,100,0,0.5);position:absolute;left:0px;top:-20px;"></div>
            <div style="width:1px;height:14px;background:rgba(255,100,0,0.5);position:absolute;left:0px;top:6px;"></div>
            <div style="width:14px;height:1px;background:rgba(255,100,0,0.5);position:absolute;left:-20px;top:0px;"></div>
            <div style="width:14px;height:1px;background:rgba(255,100,0,0.5);position:absolute;left:6px;top:0px;"></div>
            <div style="width:4px;height:4px;background:rgba(255,100,0,0.8);border-radius:50%;position:absolute;left:-2px;top:-2px;"></div>`;
        } else if (wn === 'Assault Rifle') {
          // AR: cross with small circle
          chContainer.innerHTML = `
            <div style="width:2px;height:10px;background:#fff;opacity:0.7;position:absolute;left:-1px;top:-14px;"></div>
            <div style="width:2px;height:10px;background:#fff;opacity:0.7;position:absolute;left:-1px;top:4px;"></div>
            <div style="width:10px;height:2px;background:#fff;opacity:0.7;position:absolute;left:-14px;top:-1px;"></div>
            <div style="width:10px;height:2px;background:#fff;opacity:0.7;position:absolute;left:4px;top:-1px;"></div>
            <div style="width:10px;height:10px;border:1px solid rgba(255,255,255,0.4);border-radius:50%;position:absolute;left:-5px;top:-5px;"></div>
            <div style="width:2px;height:2px;background:rgba(255,50,50,0.8);position:absolute;left:-1px;top:-1px;"></div>`;
        } else if (wn === 'Grenade') {
          // Grenade: arc indicator
          chContainer.innerHTML = `
            <div style="width:20px;height:20px;border:2px dashed rgba(255,255,100,0.5);border-radius:50%;position:absolute;left:-10px;top:-10px;"></div>
            <div style="width:3px;height:3px;background:rgba(255,255,100,0.7);border-radius:50%;position:absolute;left:-1px;top:-1px;"></div>`;
        } else {
          // Pistol / default: cross
          chContainer.innerHTML = `
            <div style="width:2px;height:12px;background:#fff;opacity:0.7;position:absolute;left:-1px;top:-16px;"></div>
            <div style="width:2px;height:12px;background:#fff;opacity:0.7;position:absolute;left:-1px;top:4px;"></div>
            <div style="width:12px;height:2px;background:#fff;opacity:0.7;position:absolute;left:-16px;top:-1px;"></div>
            <div style="width:12px;height:2px;background:#fff;opacity:0.7;position:absolute;left:4px;top:-1px;"></div>
            <div style="width:2px;height:2px;background:rgba(255,50,50,0.8);position:absolute;left:-1px;top:-1px;"></div>`;
        }
      }
    }

    // Minimap
    if (data.playerX !== undefined && data.playerZ !== undefined) {
      this.drawMinimap(data.playerX, data.playerZ, data.playerRotY ?? 0, data.exitDoorX, data.exitDoorZ, data.stageId ?? 1);
    }
  }

  showDamageOverlay(): void {
    const overlay = this.hudElement?.querySelector('#damage-overlay') as HTMLElement;
    if (overlay) {
      overlay.style.borderColor = 'rgba(255, 0, 0, 0.5)';
      setTimeout(() => { overlay.style.borderColor = 'transparent'; }, 200);
    }
  }

  private interactHint: HTMLElement | null = null;

  showInteractHint(text: string): void {
    if (!this.interactHint) {
      this.interactHint = document.createElement('div');
      this.interactHint.style.cssText = `
        position:fixed;bottom:25%;left:50%;transform:translateX(-50%);
        color:#ffcc00;font-size:1.1rem;text-align:center;
        text-shadow:0 0 10px rgba(0,0,0,1);
        z-index:25;pointer-events:none;
        background:rgba(0,0,0,0.4);padding:8px 20px;border-radius:4px;
      `;
      document.body.appendChild(this.interactHint);
    }
    this.interactHint.textContent = text;
    // Auto-hide after frame
    setTimeout(() => {
      if (this.interactHint) {
        this.interactHint.remove();
        this.interactHint = null;
      }
    }, 200);
  }

  // ==================== PAUSE ====================
  private showPause(): void {
    const btnStyle = 'width:250px;padding:12px 0;background:rgba(255,255,255,0.05);border:2px solid #555;color:#ccc;font-size:1.1rem;cursor:pointer;';
    this.setScreen(`
      <div style="width:100%;height:100%;background:rgba(0,0,0,0.8);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;">
        <div style="font-size:2.5rem;color:#ff4444;margin-bottom:1rem;letter-spacing:0.3em;">${t('pause.title')}</div>
        <button data-action="resume-game" style="${btnStyle}">${t('pause.resume')}</button>
        <button data-action="pause-settings" style="${btnStyle}">${t('pause.settings')}</button>
        <button data-action="pause-home" style="${btnStyle}">${t('pause.home')}</button>
      </div>
    `);
  }

  // ==================== LEVEL CLEAR ====================
  private showLevelClear(data: { stage: number; level: number; stats: { kills: number; coins: number; time: number } }): void {
    const timeStr = `${Math.floor(data.stats.time / 60)}:${String(Math.floor(data.stats.time % 60)).padStart(2, '0')}`;
    const hasNext = data.level < 10 || (data.level === 10 && data.stage < 10);
    let nextStage = data.stage;
    let nextLevel = data.level + 1;
    if (nextLevel > 10) { nextStage++; nextLevel = 1; }

    this.setScreen(`
      <div style="width:100%;height:100%;background:rgba(0,0,0,0.85);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;">
        <div style="font-size:3rem;color:#44ff44;font-weight:bold;text-shadow:0 0 30px rgba(0,255,0,0.5);letter-spacing:0.3em;margin-bottom:1rem;">${t('clear.title')}</div>
        <div style="text-align:center;margin-bottom:1.5rem;">
          <div style="color:#ccc;font-size:1.2rem;margin:0.5rem 0;">${t('clear.kills')}: <span style="color:#ffd700;font-weight:bold;">${data.stats.kills}</span></div>
          <div style="color:#ccc;font-size:1.2rem;margin:0.5rem 0;">${t('clear.coins')}: <span style="color:#ffd700;font-weight:bold;">${data.stats.coins}</span></div>
          <div style="color:#ccc;font-size:1.2rem;margin:0.5rem 0;">${t('clear.time')}: <span style="color:#ffd700;font-weight:bold;">${timeStr}</span></div>
        </div>
        ${hasNext ? `<button data-action="next-level" data-stage="${nextStage}" data-level="${nextLevel}" style="width:250px;padding:12px 0;border:2px solid #44ff44;background:rgba(0,255,0,0.15);color:#44ff44;font-size:1.1rem;cursor:pointer;font-weight:bold;">${t('clear.nextLevel')}</button>` : ''}
        <button data-action="clear-home" style="width:250px;padding:12px 0;border:2px solid #555;background:rgba(255,255,255,0.05);color:#aaa;font-size:1.1rem;cursor:pointer;font-weight:bold;margin-top:0.5rem;">${t('clear.home')}</button>
      </div>
    `);
  }

  // ==================== GAME OVER ====================
  private showGameOver(): void {
    this.setScreen(`
      <div style="width:100%;height:100%;background:rgba(20,0,0,0.9);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1.5rem;">
        <div style="font-size:3.5rem;color:#ff0000;font-weight:bold;text-shadow:0 0 40px rgba(255,0,0,0.6);letter-spacing:0.3em;">${t('gameover.title')}</div>
        <button data-action="retry" style="width:250px;padding:14px 0;border:2px solid #ff0000;background:rgba(255,0,0,0.15);color:#ff4444;font-size:1.2rem;cursor:pointer;font-weight:bold;">${t('gameover.retry')}</button>
        <button data-action="gameover-home" style="width:250px;padding:14px 0;border:2px solid #555;background:rgba(255,255,255,0.05);color:#aaa;font-size:1.2rem;cursor:pointer;font-weight:bold;">${t('gameover.home')}</button>
      </div>
    `);
  }

  // ==================== SHOP ====================
  private showShop(activeTab: string = 'buy'): void {
    this.clearScreen();
    const weapons = this.saveManager.getData().playerData.weapons;
    const coins = this.saveManager.getCoins();

    let content = '';
    if (activeTab === 'buy') {
      content = Object.entries(WEAPONS).map(([id, config]) => {
        const owned = weapons[id]?.owned;
        const canBuy = !owned && coins >= config.price && config.price > 0;
        return `
          <div style="width:200px;padding:15px;border:1px solid #444;background:rgba(255,255,255,0.03);display:flex;flex-direction:column;gap:8px;">
            <div style="color:#fff;font-weight:bold;">${t(config.nameKey)}</div>
            <div style="color:#aaa;font-size:0.8rem;">${t('weapon.damage')}: ${config.damage} | ${t('weapon.magazine')}: ${config.magazineSize}</div>
            <div style="color:#ffd700;font-size:0.9rem;">${config.price > 0 ? config.price + ' ' + t('shop.coins') : '-'}</div>
            ${owned
              ? `<div style="padding:8px;border:1px solid #44ff44;color:#44ff44;text-align:center;">${t('shop.purchased')}</div>`
              : canBuy
                ? `<button data-action="shop-buy" data-weapon="${id}" style="padding:8px;border:1px solid #ffd700;color:#ffd700;cursor:pointer;background:rgba(255,215,0,0.1);">${t('shop.buy')}</button>`
                : `<div style="padding:8px;border:1px solid #555;color:#555;text-align:center;">${t('shop.buy')}</div>`
            }
          </div>
        `;
      }).join('');
    } else {
      content = Object.entries(WEAPONS).filter(([id]) => weapons[id]?.owned).map(([id, config]) => {
        const level = weapons[id].level;
        const isMax = level >= 10;
        const cost = isMax ? 0 : UPGRADE.COSTS[level];
        const canUpgrade = !isMax && coins >= cost;
        return `
          <div style="width:200px;padding:15px;border:1px solid #444;background:rgba(255,255,255,0.03);display:flex;flex-direction:column;gap:8px;">
            <div style="color:#fff;font-weight:bold;">${t(config.nameKey)} ${t('weapon.level')}${level}</div>
            <div style="color:#aaa;font-size:0.8rem;">
              ${t('weapon.damage')}: ${Math.round(config.damage * UPGRADE.DAMAGE_MULTIPLIERS[level - 1])}
              → ${isMax ? 'MAX' : Math.round(config.damage * UPGRADE.DAMAGE_MULTIPLIERS[level])}
            </div>
            <div style="color:#ffd700;font-size:0.9rem;">${isMax ? '' : cost + ' ' + t('shop.coins')}</div>
            ${isMax
              ? `<div style="padding:8px;border:1px solid #44ff44;color:#44ff44;text-align:center;">${t('shop.maxLevel')}</div>`
              : canUpgrade
                ? `<button data-action="shop-upgrade" data-weapon="${id}" style="padding:8px;border:1px solid #ffd700;color:#ffd700;cursor:pointer;background:rgba(255,215,0,0.1);">${t('shop.upgrade')}</button>`
                : `<div style="padding:8px;border:1px solid #555;color:#555;text-align:center;">${t('shop.upgrade')}</div>`
            }
          </div>
        `;
      }).join('');
    }

    const tabStyle = (active: boolean) =>
      `padding:10px 30px;border:2px solid ${active ? '#ffd700' : '#555'};color:${active ? '#ffd700' : '#888'};cursor:pointer;font-size:1rem;background:${active ? 'rgba(255,215,0,0.1)' : 'transparent'};`;

    this.setScreen(`
      <div style="width:100%;height:100%;background:rgba(0,0,0,0.95);display:flex;flex-direction:column;align-items:center;padding:2rem;overflow-y:auto;">
        <div style="font-size:2rem;color:#ffd700;margin-bottom:0.5rem;letter-spacing:0.2em;">${t('shop.title')}</div>
        <div style="color:#ffd700;font-size:1.2rem;margin-bottom:1rem;">${t('shop.coins')}: ${coins}</div>
        <div style="display:flex;gap:0;margin-bottom:1.5rem;">
          <button data-action="shop-tab-buy" style="${tabStyle(activeTab === 'buy')}">${t('shop.tab.weapons')}</button>
          <button data-action="shop-tab-upgrade" style="${tabStyle(activeTab === 'upgrade')}">${t('shop.tab.upgrade')}</button>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:1rem;justify-content:center;max-width:800px;">
          ${content}
        </div>
        <button data-action="shop-back" style="margin-top:1.5rem;padding:10px 30px;background:rgba(255,255,255,0.05);border:2px solid #555;color:#aaa;cursor:pointer;font-size:1rem;">${t('shop.back')}</button>
      </div>
    `);
  }

  // ==================== SETTINGS ====================
  private showSettings(fromPause = false): void {
    this.clearScreen();
    const settings = this.saveManager.getSettings();
    const fp = fromPause ? 'true' : 'false';

    const optStyle = (active: boolean) =>
      `padding:8px 20px;border:2px solid ${active ? '#ff4444' : '#555'};color:${active ? '#ff4444' : '#888'};cursor:pointer;background:${active ? 'rgba(255,0,0,0.1)' : 'transparent'};font-size:1rem;`;

    this.setScreen(`
      <div style="width:100%;height:100%;background:rgba(0,0,0,0.95);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2rem;">
        <div style="font-size:2rem;color:#ff4444;letter-spacing:0.2em;">${t('settings.title')}</div>
        <div style="text-align:center;">
          <div style="color:#888;font-size:1rem;margin-bottom:0.5rem;">${t('settings.language')}</div>
          <div style="display:flex;gap:0.5rem;justify-content:center;">
            <button data-action="set-lang" data-lang="ja" data-from-pause="${fp}" style="${optStyle(settings.language === 'ja')}">日本語</button>
            <button data-action="set-lang" data-lang="en" data-from-pause="${fp}" style="${optStyle(settings.language === 'en')}">English</button>
          </div>
        </div>
        <div style="text-align:center;">
          <div style="color:#888;font-size:1rem;margin-bottom:0.5rem;">${t('settings.difficulty')}</div>
          <div style="display:flex;gap:0.5rem;justify-content:center;">
            <button data-action="set-diff" data-diff="easy" data-from-pause="${fp}" style="${optStyle(settings.difficulty === 'easy')}">${t('difficulty.easy')}</button>
            <button data-action="set-diff" data-diff="normal" data-from-pause="${fp}" style="${optStyle(settings.difficulty === 'normal')}">${t('difficulty.normal')}</button>
            <button data-action="set-diff" data-diff="hard" data-from-pause="${fp}" style="${optStyle(settings.difficulty === 'hard')}">${t('difficulty.hard')}</button>
          </div>
        </div>
        <button data-action="settings-back" data-from-pause="${fp}" style="padding:10px 30px;background:rgba(255,255,255,0.05);border:2px solid #555;color:#aaa;cursor:pointer;font-size:1rem;margin-top:1rem;">${t('settings.back')}</button>
      </div>
    `);
  }
}
